import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTheme } from "@mui/material/styles";
import { Stack } from "@mui/system";

import { getPeopleByAddress } from "api/Community/AddressesAPI";
import { peopleAPI } from "api/Community/PeopleAPI";
import { Person } from "api/Community/CommunityInterfaces";

import PeoplePickerDialog from "components/Flow/Controls/PeoplePickerDialog";
import { RectangularTextButton, RoundIconButton } from "components/Base/Buttons";

interface AddressPeopleTabProps {
  addressId: number;
}

const AddressPeopleTab: React.FC<AddressPeopleTabProps> = ({ addressId }) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelected, setPickerSelected] = useState<Person[]>([]);
  const [savingLinks, setSavingLinks] = useState(false);

  const primaryColor = theme.custom.modules.community.primary.dark;
  const textColor = theme.palette.getContrastText(primaryColor);

  const loadPeople = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await getPeopleByAddress(addressId);
      setPeople(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Failed to load people for address:", err);
      setError("Failed to load people linked to this address.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (addressId) loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressId]);

  const toNumberId = (value: any): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (
      typeof value === "string" &&
      value.trim() !== "" &&
      !Number.isNaN(Number(value))
    ) {
      return Number(value);
    }
    if (typeof value === "object" && value?.id != null) return toNumberId(value.id);
    return null;
  };

  const getCurrentAddressIdsFromPerson = (person: any): number[] => {
    const ids: number[] = [];

    if (Array.isArray(person?.addresses)) {
      for (const a of person.addresses) {
        const id = toNumberId(a?.id ?? a);
        if (id != null) ids.push(id);
      }
      return Array.from(new Set(ids)).sort((a, b) => a - b);
    }

    if (Array.isArray(person?.address_ids)) {
      for (const a of person.address_ids) {
        const id = toNumberId(a?.id ?? a);
        if (id != null) ids.push(id);
      }
      return Array.from(new Set(ids)).sort((a, b) => a - b);
    }

    return [];
  };

  const sameIdList = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };

  const uniqSorted = (arr: number[]) =>
    Array.from(new Set(arr)).sort((x, y) => x - y);

  const updatePersonAddresses = async (
    personId: number,
    nextAddressIds: number[]
  ) => {
    const payload = { address_ids: nextAddressIds };

    if (typeof (peopleAPI as any).updateField === "function") {
      await (peopleAPI as any).updateField(personId, "address_ids", nextAddressIds);
      return;
    }

    if (typeof (peopleAPI as any).patch === "function") {
      await (peopleAPI as any).patch(personId, payload);
      return;
    }

    if (typeof (peopleAPI as any).update === "function") {
      await (peopleAPI as any).update(personId, payload);
      return;
    }

    throw new Error("No compatible peopleAPI update method found.");
  };

  const openPicker = () => {
    setPickerSelected((people || []) as Person[]);
    setPickerOpen(true);
  };

  const handleSaveLinks = async () => {
    setSavingLinks(true);
    setError(null);

    try {
      const selectedIds = new Set<number>();
      for (const p of pickerSelected || []) {
        const id = toNumberId((p as any)?.id);
        if (id != null) selectedIds.add(id);
      }

      const currentlyLinkedIds = new Set<number>();
      for (const p of people || []) {
        const id = toNumberId((p as any)?.id);
        if (id != null) currentlyLinkedIds.add(id);
      }

      const toUpdateIds = uniqSorted([
        ...Array.from(selectedIds),
        ...Array.from(currentlyLinkedIds),
      ]);

      for (const personId of toUpdateIds) {
        const shouldHaveAddress = selectedIds.has(personId);

        let fresh: any = null;

        if (typeof (peopleAPI as any).getById === "function") {
          fresh = await (peopleAPI as any).getById(personId);
        } else {
          fresh =
            (people || []).find((p: any) => toNumberId(p?.id) === personId) ||
            (pickerSelected || []).find((p: any) => toNumberId(p?.id) === personId);
        }

        if (!fresh) continue;

        const currentAddressIds = getCurrentAddressIdsFromPerson(fresh);

        const nextAddressIds = shouldHaveAddress
          ? uniqSorted([...currentAddressIds, addressId])
          : currentAddressIds.filter((id) => id !== addressId);

        if (sameIdList(currentAddressIds, nextAddressIds)) continue;

        await updatePersonAddresses(personId, nextAddressIds);
      }

      setPickerOpen(false);
      await loadPeople();
    } catch (e) {
      console.error("Failed to update linked people:", e);
      setError("Failed to update linked people.");
    } finally {
      setSavingLinks(false);
    }
  };

  const fetchAllPeople = async () => {
    const res = await peopleAPI.getAll();
    return Array.isArray(res) ? res : [];
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const pickerDialog = (
    <PeoplePickerDialog
      open={pickerOpen}
      title="Manage People for this Address"
      fetchPeople={fetchAllPeople}
      selected={pickerSelected}
      onSelectedChange={setPickerSelected}
      confirmLabel={savingLinks ? "Saving..." : "Save People"}
      onClose={() => setPickerOpen(false)}
      onConfirm={handleSaveLinks}
      helperText="Select people to link to this address. Remove people to unlink. You can also create a new person."
    />
  );

  if (!people.length) {
    return (
      <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <RectangularTextButton
          colour="highlighted"
          primaryColor={primaryColor}
          textColor={textColor}
          onClick={openPicker}
          sx={{ alignSelf: "flex-start" }}
        >
          Manage People
        </RectangularTextButton>

        <Typography color="text.secondary">
          No people currently linked to this address.
        </Typography>

        {pickerDialog}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <RectangularTextButton
        colour="highlighted"
        primaryColor={primaryColor}
        textColor={textColor}
        onClick={openPicker}
        sx={{ alignSelf: "flex-start" }}
      >
        Manage People
      </RectangularTextButton>

      {pickerDialog}

      {people.map((person) => {
        const fullName =
          [person?.first_name, person?.last_name].filter(Boolean).join(" ") ||
          "Unnamed person";

        const phone = person?.phone || "";
        const email = person?.email || "";

        return (
          <Card key={person.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                backgroundColor:
                  theme.custom.modules.community.primary?.light || "action.hover",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }} noWrap>
                  {fullName}
                </Typography>
              </Box>

              {person?.id && (
                <RoundIconButton
                  colour="coloured"
                  displaySize="inline"
                  primaryColor={primaryColor}
                  iconColor={textColor}
                  icon={<LaunchIcon fontSize="small" />}
                  onClick={() =>
                    window.open(
                      `/community/people/${person.id}`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Box>

            {(phone || email) && (
              <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={2}>
                  <Typography>{phone}</Typography>
                  <Typography>{email}</Typography>
                </Stack>
              </CardContent>
            )}
          </Card>
        );
      })}
    </Box>
  );
};

export default AddressPeopleTab;